import { Avatar } from '@mantine/core';
import { IconMessageCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { DashboardAlbumDto } from 'src/Services/API/generated';
import { trackHomeCardLinkClicked } from 'src/Services/Mixpanel/events';
import { DashboardCommunityPostDto } from 'src/types/dashboard.types';
import { stripMentionMarkup } from 'src/Utils/renderMentions';
import { stripWhatsappFormatting } from 'src/Utils/whatsappFormatting';

import { TwoColumnCard } from '../TwoColumnCard';

import styles from './CommunityCard.module.scss';

const ALBUM_POST_TYPE = 'POST_MEMORY';
const MAX_TILES = 3;

interface Props {
  posts: DashboardCommunityPostDto[];
  albums: DashboardAlbumDto[];
}

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase();

const firstNameOf = (name: string) => name.trim().split(' ')[0] ?? name;

const CommunityCard = ({ posts, albums }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();

  const messages = posts.filter(post => post.type !== ALBUM_POST_TYPE);

  const openPost = (postId: string) => navigate(`/asset/${assetId}/community/post/${postId}`);
  const openAlbum = (albumId: string) => navigate(`/asset/${assetId}/community/post/${albumId}`);
  const openCreateAlbum = () => navigate(`/asset/${assetId}/community`);

  const buildMessageItem = (post: DashboardCommunityPostDto) => (
    <div key={post.id} className={styles.message} onClick={() => openPost(post.id)}>
      <Avatar src={post.authorPictureUrl} size={28} radius="xl">
        {initialOf(post.authorName)}
      </Avatar>
      <div className={styles.messageBody}>
        <div className={styles.messageMeta}>
          <span className={styles.messageAuthor}>{firstNameOf(post.authorName)}</span>
          <span className={styles.messageTime}>{dayjs(post.createdAt).fromNow()}</span>
        </div>
        {post.content && (
          <p className={styles.messageText}>
            {stripMentionMarkup(stripWhatsappFormatting(post.content))}
          </p>
        )}
      </div>
    </div>
  );

  const buildAlbumTile = (album: DashboardAlbumDto) => (
    <button
      type="button"
      key={album.id}
      className={styles.album}
      onClick={() => openAlbum(album.id)}
      style={album.coverUrl ? { backgroundImage: `url(${album.coverUrl})` } : undefined}
    >
      <span className={styles.albumTitle}>{album.title}</span>
    </button>
  );

  const messageItems = messages.map(buildMessageItem);

  const albumItems =
    albums.length > 0
      ? [
          <div key="albums" className={styles.albums}>
            {albums.slice(0, MAX_TILES).map(buildAlbumTile)}
          </div>,
        ]
      : [];
  if (albumItems.length === 0) {
    albumItems.push(
      <button
        key="create"
        type="button"
        className={styles.createAlbumLink}
        onClick={openCreateAlbum}
      >
        {t('dashboard.cards.community.createAlbum', 'Créer un album')}
      </button>
    );
  }

  return (
    <TwoColumnCard
      title={t('dashboard.cards.community.title', 'Entre nous - le Tchat')}
      linkLabel={t('dashboard.cards.community.link', 'Ouvrir Entre nous')}
      linkTo={`/asset/${assetId}/community`}
      onLinkClick={() => trackHomeCardLinkClicked({ card: 'community' })}
      accent="terracotta"
      icon={<IconMessageCircle size={20} stroke={1.75} />}
      left={{
        title: t('dashboard.cards.community.messages', 'Derniers messages'),
        count: messages.length,
        items: messageItems.slice(0, MAX_TILES),
        empty: t('dashboard.cards.community.messagesEmpty', 'Aucun message'),
      }}
      right={{
        title: t('dashboard.cards.community.albums', 'Derniers albums'),
        count: albums.length,
        items: albumItems,
        empty: t('dashboard.cards.community.albumsEmpty', 'Aucun album'),
      }}
    />
  );
};

export default CommunityCard;
